import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Box, Grid, Typography, alpha, Divider } from "@mui/material";
import AuthorizationLatterUpload from "./AuthorizationLatterUpload";
import downloadWord from "../../Assets/Board_Resolution.docx";
import { useSelector } from "react-redux";
import { ContainedButton } from "../../Common/Buttons";
import FolderCopyOutlinedIcon from "@mui/icons-material/FolderCopyOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";

const CompanyDocumentUpload = ({
  handleSteps,
  handleBack,
  userDetails,
  setIsloading,
  steps,
  getUserDetails,
}) => {
  const { btncolor } = useSelector((state) => state);
  const uuid = localStorage.getItem("uuid");
  const token = localStorage.getItem("access_token");
  const [payload, setPayload] = useState();
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    trigger,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: userDetails?.enterpriseUser,
  });

  const fileValidation = {
    required: "This field is required",
    validate: (fileList) => {
      const file = fileList[0];
      if (!file) return "This field is required";
      const validTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jfif",
        "image/heic",
      ];
      if (!validTypes.includes(file.type)) {
        return "Invalid file type";
      }
      if (file.size > 6 * 1024 * 1024) {
        return "MAX limit 6 MB.";
      }
      return true;
    },
  };

  const onSubmit = async (data, action) => {
    const updatedPayload = {
      incorporationCertificate: data?.incorporationCertificate[0],
      memorandum: data?.memorandum[0],
      associationArticles: data?.associationArticles[0],
      incumbencyCertificate: data?.incumbencyCertificate[0],
      directorsRegister: data?.directorsRegister[0],
      shareholdersRegister: data?.shareholdersRegister[0],
      boardResolution: data?.boardResolution[0],
      addressProof: data?.addressProof[0],
    };
    setPayload((prev) => ({ ...prev, ...updatedPayload }));
    handleSteps();
  };

  const uploadDocs = [
    {
      key: "incorporationCertificate",
      label: "Certificate of Incorporation *",
      hint: "Please add .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, or .HEIC file. MAX limit 6 MB.",
      validation: fileValidation,
    },
    {
      key: "memorandum",
      label: "Memorandum or Operating Agreement *",
      hint: "Please add .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, or .HEIC file. MAX limit 6 MB.",
      validation: fileValidation,
    },
    {
      key: "associationArticles",
      label: "Articles of Association / Incorporation *",
      hint: "Please add .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, or .HEIC file. MAX limit 6 MB.",
      validation: fileValidation,
    },
    {
      key: "incumbencyCertificate",
      label: "Business Registration Document or Certificate of Incumbency (issued within the last 6 months) *",
      hint: "Please add .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, or .HEIC file. MAX limit 6 MB.",
      validation: fileValidation,
    },
    {
      key: "directorsRegister",
      label: "Register of Directors *",
      hint: "Please add .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, or .HEIC file. MAX limit 6 MB.",
      validation: fileValidation,
    },
    {
      key: "shareholdersRegister",
      label: "Register of Shareholders *",
      hint: "Please add .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, or .HEIC file. MAX limit 6 MB.",
      validation: fileValidation,
    },
    {
      key: "boardResolution",
      label: null, // special — includes download link
      hint: "Please add .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, or .HEIC file. MAX limit 6 MB.",
      validation: fileValidation,
    },
    {
      key: "addressProof",
      label: "Proof of Address (issued within the last 3 months, showing company business address) *",
      hint: "Please add .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, or .HEIC file. MAX limit 6 MB.",
      validation: fileValidation,
    },
  ];

  return (
    <>
      {steps === 3 && (
        <Box sx={card}>
          {/* Step Header */}
          <Box sx={stepHeader}>
            <Box sx={{ ...stepBadge, backgroundColor: alpha(btncolor || "#1976d2", 0.12), color: btncolor }}>
              <FolderCopyOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={stepTitle}>Company Documents Upload</Typography>
              <Typography sx={stepCaption}>
                Upload all required corporate documents
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ borderColor: "rgba(125,125,125,0.13)", mb: 3 }} />

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* Certificate of Incorporation */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography sx={fieldLabel}>Certificate of Incorporation *</Typography>
                <Box sx={uploadCard}>
                  <Box sx={uploadIconRow}>
                    <UploadFileOutlinedIcon sx={{ fontSize: 16, color: btncolor, opacity: 0.8 }} />
                    <Typography sx={uploadHintInner}>Click to select file</Typography>
                  </Box>
                  <input
                    type="file"
                    style={fileInputStyle}
                    onChange={(e) => { setValue("incorporationCertificate", e.target.files); trigger("incorporationCertificate"); }}
                    {...register("incorporationCertificate", fileValidation)}
                  />
                </Box>
                {errors.incorporationCertificate && <Typography sx={errorText}>{errors.incorporationCertificate.message}</Typography>}
                <Typography sx={hintText}>Accepted: .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, .HEIC — MAX 6 MB</Typography>
              </Grid>

              {/* Memorandum */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography sx={fieldLabel}>Memorandum or Operating Agreement *</Typography>
                <Box sx={uploadCard}>
                  <Box sx={uploadIconRow}>
                    <UploadFileOutlinedIcon sx={{ fontSize: 16, color: btncolor, opacity: 0.8 }} />
                    <Typography sx={uploadHintInner}>Click to select file</Typography>
                  </Box>
                  <input
                    type="file"
                    style={fileInputStyle}
                    onChange={(e) => { setValue("memorandum", e.target.files); trigger("memorandum"); }}
                    {...register("memorandum", fileValidation)}
                  />
                </Box>
                {errors.memorandum && <Typography sx={errorText}>{errors.memorandum.message}</Typography>}
                <Typography sx={hintText}>Accepted: .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, .HEIC — MAX 6 MB</Typography>
              </Grid>

              {/* Articles of Association */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography sx={fieldLabel}>Articles of Association / Incorporation *</Typography>
                <Box sx={uploadCard}>
                  <Box sx={uploadIconRow}>
                    <UploadFileOutlinedIcon sx={{ fontSize: 16, color: btncolor, opacity: 0.8 }} />
                    <Typography sx={uploadHintInner}>Click to select file</Typography>
                  </Box>
                  <input
                    type="file"
                    style={fileInputStyle}
                    onChange={(e) => { setValue("associationArticles", e.target.files); trigger("associationArticles"); }}
                    {...register("associationArticles", fileValidation)}
                  />
                </Box>
                {errors.associationArticles && <Typography sx={errorText}>{errors.associationArticles.message}</Typography>}
                <Typography sx={hintText}>Accepted: .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, .HEIC — MAX 6 MB</Typography>
              </Grid>

              {/* Incumbency Certificate */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography sx={fieldLabel}>Business Registration / Certificate of Incumbency *</Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "var(--text-color)", opacity: 0.45, mb: 0.5 }}>
                  Issued within the last 6 months
                </Typography>
                <Box sx={uploadCard}>
                  <Box sx={uploadIconRow}>
                    <UploadFileOutlinedIcon sx={{ fontSize: 16, color: btncolor, opacity: 0.8 }} />
                    <Typography sx={uploadHintInner}>Click to select file</Typography>
                  </Box>
                  <input
                    type="file"
                    style={fileInputStyle}
                    onChange={(e) => { setValue("incumbencyCertificate", e.target.files); trigger("incumbencyCertificate"); }}
                    {...register("incumbencyCertificate", fileValidation)}
                  />
                </Box>
                {errors.incumbencyCertificate && <Typography sx={errorText}>{errors.incumbencyCertificate.message}</Typography>}
                <Typography sx={hintText}>Accepted: .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, .HEIC — MAX 6 MB</Typography>
              </Grid>

              {/* Directors Register */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography sx={fieldLabel}>Register of Directors *</Typography>
                <Box sx={uploadCard}>
                  <Box sx={uploadIconRow}>
                    <UploadFileOutlinedIcon sx={{ fontSize: 16, color: btncolor, opacity: 0.8 }} />
                    <Typography sx={uploadHintInner}>Click to select file</Typography>
                  </Box>
                  <input
                    type="file"
                    style={fileInputStyle}
                    onChange={(e) => { setValue("directorsRegister", e.target.files); trigger("directorsRegister"); }}
                    {...register("directorsRegister", fileValidation)}
                  />
                </Box>
                {errors.directorsRegister && <Typography sx={errorText}>{errors.directorsRegister.message}</Typography>}
                <Typography sx={hintText}>Accepted: .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, .HEIC — MAX 6 MB</Typography>
              </Grid>

              {/* Shareholders Register */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography sx={fieldLabel}>Register of Shareholders *</Typography>
                <Box sx={uploadCard}>
                  <Box sx={uploadIconRow}>
                    <UploadFileOutlinedIcon sx={{ fontSize: 16, color: btncolor, opacity: 0.8 }} />
                    <Typography sx={uploadHintInner}>Click to select file</Typography>
                  </Box>
                  <input
                    type="file"
                    style={fileInputStyle}
                    onChange={(e) => { setValue("shareholdersRegister", e.target.files); trigger("shareholdersRegister"); }}
                    {...register("shareholdersRegister", fileValidation)}
                  />
                </Box>
                {errors.shareholdersRegister && <Typography sx={errorText}>{errors.shareholdersRegister.message}</Typography>}
                <Typography sx={hintText}>Accepted: .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, .HEIC — MAX 6 MB</Typography>
              </Grid>

              {/* Board Resolution */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography sx={fieldLabel}>Board Resolution *</Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "var(--text-color)", opacity: 0.45, mb: 0.5 }}>
                  Download the template{" "}
                  <a target="_blank" href={downloadWord} style={{ color: "inherit", opacity: 0.8 }}>
                    here
                  </a>
                  , fill it and upload it.
                </Typography>
                <Box sx={uploadCard}>
                  <Box sx={uploadIconRow}>
                    <UploadFileOutlinedIcon sx={{ fontSize: 16, color: btncolor, opacity: 0.8 }} />
                    <Typography sx={uploadHintInner}>Click to select file</Typography>
                  </Box>
                  <input
                    type="file"
                    style={fileInputStyle}
                    onChange={(e) => { setValue("boardResolution", e.target.files); trigger("boardResolution"); }}
                    {...register("boardResolution", fileValidation)}
                  />
                </Box>
                {errors.boardResolution && <Typography sx={errorText}>{errors.boardResolution.message}</Typography>}
                <Typography sx={hintText}>Accepted: .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, .HEIC — MAX 6 MB</Typography>
              </Grid>

              {/* Address Proof */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography sx={fieldLabel}>Proof of Address *</Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "var(--text-color)", opacity: 0.45, mb: 0.5 }}>
                  Issued within the last 3 months, showing company business address
                </Typography>
                <Box sx={uploadCard}>
                  <Box sx={uploadIconRow}>
                    <UploadFileOutlinedIcon sx={{ fontSize: 16, color: btncolor, opacity: 0.8 }} />
                    <Typography sx={uploadHintInner}>Click to select file</Typography>
                  </Box>
                  <input
                    type="file"
                    style={fileInputStyle}
                    onChange={(e) => { setValue("addressProof", e.target.files); trigger("addressProof"); }}
                    {...register("addressProof", fileValidation)}
                  />
                </Box>
                {errors.addressProof && <Typography sx={errorText}>{errors.addressProof.message}</Typography>}
                <Typography sx={hintText}>Accepted: .JPG, .PDF, .PNG, .JPEG, .DOC, .DOCX, .JFIF, .HEIC — MAX 6 MB</Typography>
              </Grid>
            </Grid>

            <Box sx={actionRow}>
              <ContainedButton
                variant="contained"
                onClick={handleBack}
                sx={{ ...submitBtn, opacity: 0.85 }}
              >
                Back
              </ContainedButton>
              <ContainedButton
                type="submit"
                variant="contained"
                sx={submitBtn}
                disabled={!isValid}
                onClick={handleSubmit((data) => onSubmit(data, "next"))}
              >
                Next
              </ContainedButton>
            </Box>
          </form>
        </Box>
      )}

      {steps > 3 && (
        <AuthorizationLatterUpload
          handleSteps={handleSteps}
          handleBack={handleBack}
          userDetails={userDetails}
          setIsloading={setIsloading}
          steps={steps}
          payload={payload}
          getUserDetails={getUserDetails}
        />
      )}
    </>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const card = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  p: { xs: 2.5, md: 3 },
};

const stepHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  mb: 2.5,
  flexWrap: "wrap",
};

const stepBadge = {
  width: 44,
  height: 44,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const stepTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const stepCaption = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.55,
  mt: 0.2,
};

const fieldLabel = {
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--text-color)",
  opacity: 0.55,
  mb: 0.5,
};

const uploadCard = {
  border: "2px dashed rgba(125,125,125,0.3)",
  borderRadius: "10px",
  p: 1.5,
  mt: 0.5,
  backgroundColor: "var(--body-color-less2)",
};

const uploadIconRow = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  mb: 0.75,
};

const uploadHintInner = {
  fontSize: "0.75rem",
  color: "var(--text-color)",
  opacity: 0.5,
};

const fileInputStyle = {
  width: "100%",
  color: "var(--text-color)",
  fontSize: "0.85rem",
};

const hintText = {
  fontSize: "0.7rem",
  color: "var(--text-color)",
  opacity: 0.4,
  mt: 0.75,
  lineHeight: 1.5,
};

const errorText = {
  fontSize: "0.72rem",
  color: "#ef4444",
  mt: 0.5,
};

const actionRow = {
  mt: 3.5,
  pt: 2.5,
  borderTop: "1px solid rgba(125,125,125,0.12)",
  display: "flex",
  gap: 1.5,
};

const submitBtn = {
  height: 48,
  px: 4,
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "0.88rem",
  textTransform: "none",
};

export default CompanyDocumentUpload;
